/**
 * Resolves a dataUsage `sourcePath` string into a human-readable breadcrumb
 * chain by dereferencing Firebase document IDs against the hydrated source
 * data graph.
 *
 * Path grammar (strategy-blocks style, adopted by content-plan in Sprint 17):
 *   @audience[<id>]
 *   @audience[<id>].actors[<id>]
 *   @audience[<id>].actors[<id>].<collectionKey>[<id>]   e.g. fears/jtbd/coreJobs
 *   @task[<id>]                                          (or @task[<id>].description)
 *   @insight[<id>]
 *   @insight[<id>].aggregatedInsights.<category>[<id>]
 *   @review[<id>] / @studyCase[<id>] / @bigNumber[<id>]
 *
 * Legacy index-based paths (`audiences[0].actors[1]`) are NOT supported —
 * the resolver returns `null` and callers fall back to showing the raw string
 * so existing Firestore docs remain readable while new writes migrate forward.
 */

import type { HydratedSourceData } from '@modules/shared-types';

export type BreadcrumbKind =
  | 'audience'
  | 'actor'
  | 'task'
  | 'insight'
  | 'review'
  | 'studyCase'
  | 'bigNumber'
  | 'jtbd'
  | 'fear'
  | 'desire'
  | 'pain'
  | 'gain'
  | 'motivation'
  | 'coreJob'
  | 'aggregatedInsight'
  | 'other';

export interface Breadcrumb {
  kind: BreadcrumbKind;
  label: string;
  id: string;
}

export interface ResolvedPath {
  raw: string;
  breadcrumbs: Breadcrumb[];
  /** Terminal property name when the path ends on a scalar field (e.g. `.description`). */
  terminalField?: string;
}

const SEGMENT_REGEX = /^([a-zA-Z]+)(?:\[([^\]]+)\])?$/;
const ROOT_PREFIX = '@';
const ACTOR_SUBCOLLECTIONS: Record<string, BreadcrumbKind> = {
  jtbd: 'jtbd',
  fears: 'fear',
  desires: 'desire',
  pains: 'pain',
  gains: 'gain',
  motivations: 'motivation',
  coreJobs: 'coreJob',
};

function pickLabel(obj: Record<string, unknown> | undefined, fallback: string): string {
  if (!obj) return fallback;
  const candidate =
    (typeof obj.name === 'string' && obj.name) ||
    (typeof obj.title === 'string' && obj.title) ||
    (typeof obj.description === 'string' && obj.description) ||
    (typeof obj.summary === 'string' && obj.summary) ||
    (typeof obj.reviewtext === 'string' && obj.reviewtext);
  if (!candidate) return fallback;
  return candidate.length > 80 ? `${candidate.slice(0, 77)}…` : candidate;
}

function findById<T extends { id?: string }>(
  arr: T[] | undefined,
  id: string,
): T | undefined {
  return arr?.find((item) => String(item?.id ?? '') === id);
}

export function resolveSourcePath(
  path: string,
  source: HydratedSourceData | null | undefined,
): ResolvedPath | null {
  if (!path || !source) return null;
  if (!path.startsWith(ROOT_PREFIX)) return null;

  const segments = path.slice(1).split('.');
  if (segments.length === 0) return null;

  const first = segments[0].match(SEGMENT_REGEX);
  if (!first) return null;
  const [, rootKind, rootId] = first;
  if (!rootId) return null;

  const breadcrumbs: Breadcrumb[] = [];
  let terminalField: string | undefined;

  switch (rootKind) {
    case 'audience': {
      const audience = findById(source.audiences, rootId);
      if (!audience) return null;
      breadcrumbs.push({
        kind: 'audience',
        label: pickLabel(audience as Record<string, unknown>, rootId),
        id: rootId,
      });
      // Walk remaining segments (.actors[id].<key>[id])
      let i = 1;
      if (i < segments.length) {
        const actorsMatch = segments[i].match(SEGMENT_REGEX);
        if (!actorsMatch || actorsMatch[1] !== 'actors') {
          if (!actorsMatch?.[2]) terminalField = segments[i];
          return { raw: path, breadcrumbs, terminalField };
        }
        const actorId = actorsMatch[2];
        if (!actorId) return { raw: path, breadcrumbs };
        const actor = findById(audience.actors, actorId);
        if (!actor) return null;
        breadcrumbs.push({
          kind: 'actor',
          label: pickLabel(actor as Record<string, unknown>, actorId),
          id: actorId,
        });
        i += 1;
        if (i < segments.length) {
          const subMatch = segments[i].match(SEGMENT_REGEX);
          if (subMatch) {
            const [, subKey, subId] = subMatch;
            if (!subId) {
              terminalField = subKey;
            } else {
              const kind = ACTOR_SUBCOLLECTIONS[subKey] ?? 'other';
              const arr = (actor as Record<string, unknown>)[subKey];
              const item = Array.isArray(arr)
                ? findById(arr as Array<{ id?: string }>, subId)
                : undefined;
              if (!item) return null;
              breadcrumbs.push({
                kind,
                label: pickLabel(item as Record<string, unknown>, subId),
                id: subId,
              });
            }
          }
        }
      }
      return { raw: path, breadcrumbs, terminalField };
    }
    case 'task': {
      const task = findById(source.tasks, rootId);
      if (!task) return null;
      breadcrumbs.push({
        kind: 'task',
        label: pickLabel(task as Record<string, unknown>, rootId),
        id: rootId,
      });
      if (segments.length > 1) terminalField = segments.slice(1).join('.');
      return { raw: path, breadcrumbs, terminalField };
    }
    case 'insight': {
      const insight = findById(source.insights, rootId);
      if (!insight) return null;
      breadcrumbs.push({
        kind: 'insight',
        label: pickLabel(insight as Record<string, unknown>, rootId),
        id: rootId,
      });
      // Optional aggregatedInsights.<category>[<id>] suffix
      if (
        segments.length >= 3 &&
        segments[1] === 'aggregatedInsights'
      ) {
        const catMatch = segments[2].match(SEGMENT_REGEX);
        if (catMatch) {
          const [, category, catId] = catMatch;
          if (catId) {
            const bucket = insight.aggregatedInsights?.[category];
            const item = Array.isArray(bucket)
              ? findById(bucket as Array<{ id?: string }>, catId)
              : undefined;
            breadcrumbs.push({
              kind: 'aggregatedInsight',
              label: item
                ? pickLabel(item as Record<string, unknown>, catId)
                : `${category}:${catId}`,
              id: catId,
            });
          } else {
            terminalField = `aggregatedInsights.${category}`;
          }
        }
      } else if (segments.length > 1) {
        terminalField = segments.slice(1).join('.');
      }
      return { raw: path, breadcrumbs, terminalField };
    }
    case 'review': {
      const review = findById(source.reviews, rootId);
      if (!review) return null;
      breadcrumbs.push({
        kind: 'review',
        label: pickLabel(review as Record<string, unknown>, rootId),
        id: rootId,
      });
      return { raw: path, breadcrumbs };
    }
    case 'studyCase': {
      const sc = findById(source.studyCases, rootId);
      if (!sc) return null;
      breadcrumbs.push({
        kind: 'studyCase',
        label: pickLabel(sc as Record<string, unknown>, rootId),
        id: rootId,
      });
      return { raw: path, breadcrumbs };
    }
    case 'bigNumber': {
      const bn = findById(source.bigNumbers, rootId);
      if (!bn) return null;
      breadcrumbs.push({
        kind: 'bigNumber',
        label: pickLabel(bn as Record<string, unknown>, rootId),
        id: rootId,
      });
      return { raw: path, breadcrumbs };
    }
    default:
      return null;
  }
}

/**
 * Convenience adapter for UIs: render breadcrumbs as a " › " chain. Returns
 * `null` when resolution fails so the caller can fall back to the raw path.
 */
export function formatResolvedPath(
  path: string,
  source: HydratedSourceData | null | undefined,
): string | null {
  const resolved = resolveSourcePath(path, source);
  if (!resolved || resolved.breadcrumbs.length === 0) return null;
  const chain = resolved.breadcrumbs.map((b) => b.label).join(' › ');
  return resolved.terminalField ? `${chain} · ${resolved.terminalField}` : chain;
}
